import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Divider,
  Drawer,
  List,
  ListItemButton,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { arrayOf, bool, func, number, shape } from 'prop-types';

import { useTheme } from '@mui/material/styles';
import {
  dockedStyles,
  guttersStyles,
  listItemStyles,
  listStyles,
  paperStyles,
  selectedStyles,
} from './Sidebar.styles';

const SELECTOR_HEIGHT = 56;

const Sidebar = ({
  open,
  parentCompanies,
  parentCompaniesIndex,
  parentCompaniesPending,
  setMobileSidebarOpen,
  setParentCompaniesIndex,
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const listRef = useRef();
  const [listHeight, setListHeight] = useState(100);

  useEffect(() => {
    setListHeight(listRef.current.getBoundingClientRect().height);
  }, []);

  const handleParentChange = index => {
    setMobileSidebarOpen(false);
    setParentCompaniesIndex(index);
  };

  const renderSkeletons = () => {
    const numberOfCardsNeeded = Math.ceil(listHeight / SELECTOR_HEIGHT);
    const skeletonArray = Array(numberOfCardsNeeded).fill(() => (
      <>
        <ListItemButton
          disabled
          sx={{ ...guttersStyles(theme), ...listItemStyles(theme), ...selectedStyles }}
        >
          <Skeleton height={24} width="100%" />
          <ChevronRightRoundedIcon />
        </ListItemButton>
        {!lgUp && <Divider />}
      </>
    ));
    // eslint-disable-next-line react/no-array-index-key
    return skeletonArray.map((Component, i) => <Component key={`skeleton${i}`} />);
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      slotProps={{
        paper: { sx: paperStyles(theme) },
      }}
      sx={{ ...dockedStyles }}
      variant="persistent"
    >
      <List disablePadding ref={listRef} sx={{ ...listStyles(theme) }}>
        {parentCompaniesPending
          ? renderSkeletons()
          : parentCompanies.length > 1 &&
            parentCompanies.map(({ customerNameShort, customerId }, i) => (
              <Fragment key={`${customerId}${customerNameShort}`}>
                <ListItemButton
                  data-testid={`parent-company-${customerNameShort}`}
                  onClick={() => handleParentChange(i)}
                  selected={parentCompaniesIndex === i && lgUp}
                  sx={{ ...guttersStyles(theme), ...listItemStyles(theme), ...selectedStyles }}
                >
                  <Typography color="textPrimary" noWrap variant="subtitle1">
                    {customerNameShort}
                  </Typography>
                  <ChevronRightRoundedIcon />
                </ListItemButton>
                {!lgUp && <Divider />}
              </Fragment>
            ))}
      </List>
    </Drawer>
  );
};

Sidebar.propTypes = {
  open: bool.isRequired,
  parentCompanies: arrayOf(shape({})).isRequired,
  parentCompaniesIndex: number,
  parentCompaniesPending: bool.isRequired,
  setMobileSidebarOpen: func.isRequired,
  setParentCompaniesIndex: func.isRequired,
};

Sidebar.defaultProps = {
  parentCompaniesIndex: null,
};

export default Sidebar;
